import { BaseIconHelper } from './base.icon.helper';
import { PowerState, ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { GridIcon } from '../types';
import { ButtonColor } from '../../../../ui/gzIconButton/types';
import { IconProperties } from '../../../../ui/gzIcon/types';
import { translateFn } from '../../../../utils/translator.helper';

export class NetworkGridIconHelper extends BaseIconHelper {
  public static getIconForProtectedEntity(protectedEntity: ProtectedEntity): IconProperties {
    if (protectedEntity.specifics.isGoldenImage) {
      return NetworkGridIconHelper.getIconForGoldenImage(protectedEntity);
    }

    switch (protectedEntity.type) {
      case Constants.Companies.TYPE_COMPANY:
        return NetworkGridIconHelper.getIconForCompany(protectedEntity);
      case Constants.Companies.TYPE_COMPANIES_COLLECTION:
        return NetworkGridIconHelper.getIconForCompaniesCollection();
      case Constants.Companies.TYPE_COMPANY_NETWORK:
        return NetworkGridIconHelper.getIconForCompanyNetwork();
      case Constants.Companies.TYPE_COMPANY_FOLDER:
      case Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP:
      case Constants.ProtectedEntities.EPS.TYPE_AD_COMPUTER_GROUP:
      case Constants.ProtectedEntities.EPS.ACTIVE_DIRECTORY:
      case Constants.ProtectedEntities.EPS.CUSTOM_GROUPS:
      case Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER:
      case Constants.ProtectedEntities.EPS.TERMINATED_INSTANCES_GROUP:
      case Constants.ProtectedEntities.CONTAINERS.TYPE_COLLECTION:
      case Constants.ProtectedEntities.CONTAINERS.TYPE_CUSTOM_GROUPS:
      case Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER:
        return NetworkGridIconHelper.getIconForFolder();
      case Constants.ProtectedEntities.EPS.TYPE_COMPUTER:
      case Constants.ProtectedEntities.EPS.TYPE_AD_COMPUTER:
        return NetworkGridIconHelper.getIconForComputer(protectedEntity);
      case Constants.ProtectedEntities.CONTAINERS.TYPE_CONTAINER:
        return NetworkGridIconHelper.getIconForContainer(protectedEntity);
      case Constants.ProtectedEntities.CONTAINERS.TYPE_HOST:
        return NetworkGridIconHelper.getIconForContainerHost(protectedEntity);
      case Constants.ProtectedEntities.EPS.TYPE_AWS_GROUP:
        return NetworkGridIconHelper.getIconForAwsGroup();
      case Constants.ProtectedEntities.EPS.TYPE_AWS_ACCOUNT:
        return NetworkGridIconHelper.getIconForAwsAccount();
      case Constants.ProtectedEntities.EPS.TYPE_AWS_REGION:
        return NetworkGridIconHelper.getIconForAwsRegion();
      case Constants.ProtectedEntities.EPS.TYPE_AWS_AZ:
        return NetworkGridIconHelper.getIconForAwsAz();
      case Constants.ProtectedEntities.EPS.TYPE_AWS_INSTANCE:
        return NetworkGridIconHelper.getIconForAwsInstance(protectedEntity);
      case Constants.ProtectedEntities.EPS.VMWARE_COLLECTION:
        return NetworkGridIconHelper.getIconForVmWareCollection();
      case Constants.ProtectedEntities.EPS.VMWARE_CENTER:
        return NetworkGridIconHelper.getIconForVmWareCenter();
      case Constants.ProtectedEntities.EPS.VMWARE_DATACENTER:
        return NetworkGridIconHelper.getIconForVmWareDatacenter();
      case Constants.ProtectedEntities.EPS.VMWARE_FOLDER:
      case Constants.ProtectedEntities.EPS.VMWARE_COMPUTE_RESOURCE:
        return NetworkGridIconHelper.getIconForFolder();
      case Constants.ProtectedEntities.EPS.VMWARE_CLUSTER_COMPUTE_RESOURCE:
      case Constants.ProtectedEntities.EPS.VMWARE_RESOURCE_POOL:
        return NetworkGridIconHelper.getIconForVmWareClusterComputeResource();
      case Constants.ProtectedEntities.EPS.VMWARE_VIRTUAL_APP:
        return NetworkGridIconHelper.getIconForVmWareClusterVirtualApp();
      case Constants.ProtectedEntities.EPS.VMWARE_HOST_SYSTEM:
        return NetworkGridIconHelper.getIconForVmWareHost();
      case Constants.ProtectedEntities.EPS.VMWARE_VIRTUAL_MACHINE:
        return NetworkGridIconHelper.getIconForComputer(protectedEntity);
      default:
        return { icon: '', color: ButtonColor.SECONDARY };
    }
  }

  private static getIconForGoldenImage(protectedEntity: ProtectedEntity): IconProperties {
    return protectedEntity.specifics.isOnline
      ? { icon: GridIcon.ICON_GOLDEN_IMAGE, color: ButtonColor.PRIMARY, isLink: true }
      : { icon: GridIcon.ICON_GOLDEN_IMAGE, color: ButtonColor.SECONDARY, isLink: true };
  }

  private static getIconForContainer(protectedEntity: ProtectedEntity): IconProperties {
    if (!protectedEntity.specifics.isManaged) {
      return {
        icon: GridIcon.ICON_CONTAINERS,
        color: ButtonColor.SECONDARY,
        isLink: true,
      };
    }

    if (protectedEntity.specifics.isSecurityContainer) {
      return protectedEntity.specifics.isOnline
        ? { icon: GridIcon.ICON_SECURITY_CONTAINER, color: ButtonColor.PRIMARY, isLink: true }
        : { icon: GridIcon.ICON_SECURITY_CONTAINER, color: ButtonColor.SECONDARY, isLink: true };
    }

    return protectedEntity.specifics.isOnline
      ? { icon: GridIcon.ICON_CONTAINERS, color: ButtonColor.PRIMARY, isLink: true }
      : { icon: GridIcon.ICON_CONTAINERS, color: ButtonColor.SECONDARY, isLink: true };
  }

  private static getIconForContainerHost(protectedEntity: ProtectedEntity): IconProperties {
    if (!protectedEntity.specifics.isManaged) {
      return {
        icon: GridIcon.ICON_CONTAINER_HOSTS,
        color: ButtonColor.SECONDARY,
        isLink: true,
      };
    }

    return protectedEntity.specifics.isOnline
      ? {
          icon: GridIcon.ICON_CONTAINER_HOSTS,
          color: ButtonColor.PRIMARY,
          isLink: true,
        }
      : {
          icon: GridIcon.ICON_CONTAINER_HOSTS,
          color: ButtonColor.SECONDARY,
          isLink: true,
        };
  }

  private static getIconForCompany(protectedEntity: ProtectedEntity): IconProperties {
    if (protectedEntity.specifics.rights?.manageCompanies) {
      return protectedEntity.specifics.canBeManagedByAbove
        ? { icon: GridIcon.ICON_COMPANY, color: ButtonColor.SECONDARY, isLink: true }
        : {
            icon: GridIcon.ICON_COMPANY,
            color: ButtonColor.SECONDARY,
            tooltipText: translateFn('Self-managed partner company. No rights to view or make changes.'),
          };
    }

    return protectedEntity.specifics.canBeManagedByAbove
      ? { icon: GridIcon.ICON_CUSTOMER, color: ButtonColor.SECONDARY, isLink: true }
      : {
          icon: GridIcon.ICON_CUSTOMER,
          color: ButtonColor.SECONDARY,
          tooltipText: translateFn('Self-managed customer company. No rights to view or make changes.'),
        };
  }

  private static getIconForFolder(): IconProperties {
    return {
      icon: GridIcon.ICON_FOLDER,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }

  private static getIconForCompanyNetwork() {
    return {
      icon: GridIcon.ICON_NETWORK,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }

  private static getIconForCompaniesCollection() {
    return {
      icon: GridIcon.ICON_COMPANIES,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }

  private static getIconForComputer(protectedEntity: ProtectedEntity) {
    const isVirtualMachine: boolean = !!protectedEntity.specifics.isVirtual;
    const isSva: boolean = !!protectedEntity.specifics.isSva;
    const isNsva: boolean = !!protectedEntity.specifics.isNsva;
    const isVmWareIntegrator: boolean = !!protectedEntity.specifics.isVmwareSyncer;

    const icon: GridIcon = isVirtualMachine ? (isSva || isNsva ? GridIcon.ICON_SECURITY_SERVER : (isVmWareIntegrator ? GridIcon.ICON_VMWARE_INTEGRATOR : GridIcon.ICON_VIRTUAL_MACHINE)) : GridIcon.ICON_PHYSICAL_MACHINE;

    if (!protectedEntity.specifics.isManaged) {
      return {
        icon: icon,
        color: ButtonColor.SECONDARY,
        isLink: true,
      };
    }

    return protectedEntity.specifics.isOnline
      ? { icon: icon, color: ButtonColor.PRIMARY, isLink: true }
      : { icon: icon, color: ButtonColor.SECONDARY, isLink: true };
  }

  private static getIconForAwsGroup(): IconProperties {
    return {
      icon: GridIcon.ICON_AWS_GROUP,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }

  private static getIconForAwsAccount(): IconProperties {
    return {
      icon: GridIcon.ICON_AWS_ACCOUNT,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }

  private static getIconForAwsRegion(): IconProperties {
    return {
      icon: GridIcon.ICON_AWS_REGION,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }

  private static getIconForAwsAz(): IconProperties {
    return {
      icon: GridIcon.ICON_AWS_AZ,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }

  private static getIconForAwsInstance(protectedEntity: ProtectedEntity): IconProperties {
    const isOnline = protectedEntity.specifics.stateCode === PowerState.POWER_STATE_RUNNING;
    return {
      icon: GridIcon.ICON_AWS_INSTANCE,
      color: isOnline ? ButtonColor.PRIMARY : ButtonColor.SECONDARY,
      isLink: true,
    };
  }

  private static getIconForVmWareCollection(): IconProperties {
    return {
      icon: GridIcon.ICON_VMWARE_COLLECTION,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }
  private static getIconForVmWareCenter(): IconProperties {
    return {
      icon: GridIcon.ICON_VMWARE_CENTER,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }
  private static getIconForVmWareDatacenter(): IconProperties {
    return {
      icon: GridIcon.ICON_VMWARE_DATACENTER,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }
  private static getIconForVmWareClusterComputeResource(): IconProperties {
    return {
      icon: GridIcon.ICON_VMWARE_CLUSTER_COMPUTE_RESOURCE,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }
  private static getIconForVmWareClusterVirtualApp(): IconProperties {
    return {
      icon: GridIcon.ICON_VMWARE_VIRTUAL_APP,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }
  private static getIconForVmWareHost(): IconProperties {
    return {
      icon: GridIcon.ICON_VMWARE_HOST,
      color: ButtonColor.SECONDARY,
      isLink: true,
    };
  }
}
